/**
 * OVERLAYS (owner B5): the land's annotations under the light. Implements OverlaysAPI (contracts/map.ts).
 *
 *   const ov = createOverlays(map, { beforeId: ANCHORS.overlays, placesBeforeId: ANCHORS.places, reducedMotion });
 *
 * Layers, bottom → top, all just beneath ANCHORS.overlays (above the basemap roads, below the routes):
 *   ld-veil            background: the land recedes on select (§D.10), background-opacity on the ticker
 *   ld-contours-lit    "the land answers": contour runs around a destination, weight w, line-layer-opacity
 *   ld-site-fill/line  the approximate site outline (§D.4); the lit outline on top is the LightLayer's (B3)
 *   ld-rings           5/10/15/25 km geodesic rings (§D.5), per-ring opacity (feature-state "o")
 *   ld-roads-*         arterial corridors (§D.7): quiet case + line, then the lit glow, contact shade and core
 *   ld-future-line     planned corridors: dashed, never glow
 *   ld-roads-label, ld-future-label   names along the lines
 * and just beneath ANCHORS.places (above the basemap labels): ld-rings-label.
 *
 * Motion: every value that moves is written by the ticker with the brand curves (feature-state tweens for
 * per-feature values, layer/background opacity for whole layers). Colours change with the mood through
 * MapLibre paint transitions (every constant colour, so they ease; §E.8).
 *
 * The store: initial layer flags (boundary, rings, future, contours) are read at creation; the controller
 * routes later flag changes here. The overlays follow store.group (quiet arterials show while group A is
 * active, §D table), store.layout (kiosk text) and store.layers.contours (lit contours respect it).
 */
import type { ExpressionSpecification, FilterSpecification, GeoJSONSource, LayerSpecification, Map as MLMap } from "maplibre-gl";
import type { Feature, FeatureCollection, LineString, MultiLineString, Point, Polygon } from "geojson";
import type { CorridorCollection, LngLat, SiteCollection } from "@/contracts/data";
import { LAYER_IDS, SOURCE_IDS, type CreateOverlays, type EaseName, type OverlaysAPI } from "@/contracts/map";
import type { Layout, Mood } from "@/contracts/store";
import { RING_KM } from "@/contracts/taxonomy";
import { DUR, easeBy, exitMs } from "@/design";
import { siteLabelPoint } from "@/map/controller";
import { FONTSTACKS, MOODS, MOOD_TRANSITION_MS } from "@/map/style/moods";
import { appStore, getApp } from "@/store/store";
import { Animator, FeatureFx, type Tween } from "./fx";
import { futureLabelText, ringFeatures, ringLabelId, roadLabelText } from "./geometry";
import { buildLitContours, indexContours, type ContourIndex } from "./litContours";

/* ─── Ids for the layers the contract does not name ──────────────────────── */

export const OVERLAY_IDS = {
  roadsGlow: "ld-roads-glow",
  roadsShade: "ld-roads-shade",
  roadsLit: "ld-roads-lit",
  roadsLabel: "ld-roads-label",
} as const;

/** OverlaysAPI plus B5 extensions (B4 may use them through this type). */
export interface OverlaysHandle extends OverlaysAPI {
  /** `bloom`: corridors lit at bloom strength (width × 0.8, no glow) as their routes arrive (§E.4). */
  setRoads(o: { visible: boolean; highlight?: string | null; bloom?: string[] }): void;
  /** `bloom`: every planned corridor at full strength with its label (future-infrastructure chip, §E.4). */
  setFuture(o: { visible: boolean; highlight?: string | null; bloom?: boolean }): void;
  /** `fill`: 0…1 of the mood's fill opacity (the intro's site beat raises it over 640 settle). */
  setBoundary(o: { visible: boolean; fill?: number }): void;
  /**
   * delayMs: build now (hidden), rise after the delay (select: build at t = 0, rise at Tarr).
   * ease: the rise curve (default settle; the site beat uses drift). rest 0 fades them away after the rise.
   */
  lightContours(at: LngLat | null, o?: { zoom: number; radiusPx?: number; rest?: number; durationMs?: number; delayMs?: number; ease?: EaseName }): void;
  /** duration: the controller's transition (the sunrise is 2400). */
  setMood(m: Mood, o?: { duration?: number }): void;
  debug(): OverlaysDebug;
}

export interface OverlaysDebug {
  veil: number;
  lit: number;
  litSegments: number;
  litBuildMs: number;
  litIndexMs: number;
  rings: number[];
  site: { visible: number; fill: number };
  roadsHighlight: string | null;
  roadsBloom: string[];
  futureHighlight: string | null;
  group: string;
}

/* ─── Helpers ───────────────────────────────────────────────────────────── */

type Expr = ExpressionSpecification;
const fs = (k: string, d = 0): Expr => ["coalesce", ["feature-state", k], d] as Expr;
const NO_TR = { duration: 0, delay: 0 };
const EMPTY_FC: FeatureCollection = { type: "FeatureCollection", features: [] };

const cssVar = (m: Mood, k: string, fallback: string): string => MOODS[m].cssVars[k] ?? fallback;
const shadePx = (m: Mood): number => Number(MOODS[m].cssVars["--ld-route-shade-px"] ?? 0) || 0;
const glowGain = (m: Mood): number => Number(MOODS[m].cssVars["--ld-glow-gain"] ?? 1) || 1;

/** Line widths (§D.7), each a zoom interpolation whose stops may carry a feature-state factor. */
const W = {
  roadCase: ["interpolate", ["linear"], ["zoom"], 9, 3, 14, 7] as Expr,
  road: ["interpolate", ["linear"], ["zoom"], 9, 1.5, 14, 4] as Expr,
  lit: (mul: number, plus = 0): Expr => ["interpolate", ["linear"], ["zoom"], 9, ["+", ["*", 2.5 * mul, fs("wm", 1)], plus], 14, ["+", ["*", 5 * mul, fs("wm", 1)], plus]] as Expr,
  future: ["interpolate", ["linear"], ["zoom"], 9, ["*", 2.5, ["+", 1, ["*", 0.4, fs("s")]]], 14, ["*", 3.5, ["+", 1, ["*", 0.4, fs("s")]]]] as Expr,
};

/** Quiet arterials only from z10 (§D table: below it, only the highlight). */
const quietFade = (inner: Expr): Expr => ["interpolate", ["linear"], ["zoom"], 9.85, 0, 10.15, inner] as Expr;
const QUIET: Expr = ["*", fs("v", 1), fs("q"), ["-", 1, ["*", 0.6, fs("od")]], ["-", 1, fs("a")]] as Expr;
const LIT: Expr = ["*", fs("v", 1), fs("a")] as Expr;
/** Planned corridors: 0.85 at rest, 1 when selected or bloomed, 0.35 when another is selected. */
const FUTURE_REST = 0.85;
const FUTURE_OP: Expr = ["*", fs("v", 1), ["+", FUTURE_REST, ["*", 1 - FUTURE_REST, ["max", fs("s"), fs("b")]]], ["-", 1, ["*", 1 - 0.35 / FUTURE_REST, fs("od")]]] as Expr;
const FUTURE_LABEL_MAX_ANGLE = 45;
const FUTURE_LABEL_OP: Expr = ["*", fs("v", 1), ["-", 1, ["*", 0.65, fs("od")]]] as Expr;

function ringLabelOpacity(): Expr {
  const off = (z: number): Expr => ["case", ["<=", ["get", "maxz"], z], 0, fs("o", 1)] as Expr;
  return ["interpolate", ["linear"], ["zoom"], 10.75, fs("o", 1), 11, off(11), 11.75, off(11), 12, off(12), 13, off(12), 13.5, 0] as Expr;
}

function sitePolygon(site: SiteCollection | null | undefined): FeatureCollection<Polygon> {
  const f = site?.features.filter((x) => x.geometry.type === "Polygon") ?? [];
  return { type: "FeatureCollection", features: f.map((x) => ({ type: "Feature", properties: { id: x.properties.id }, geometry: x.geometry as Polygon })) };
}

function withLabels(fc: CorridorCollection | null | undefined, text: (p: CorridorCollection["features"][number]["properties"]) => string): FeatureCollection<LineString | MultiLineString> {
  const features = (fc?.features ?? []).map(
    (f): Feature<LineString | MultiLineString> => ({
      type: "Feature",
      properties: { ...f.properties, label: text(f.properties) },
      geometry: f.geometry as LineString | MultiLineString,
    }),
  );
  return { type: "FeatureCollection", features };
}

/* ═══ Factory ═══════════════════════════════════════════════════════════ */

export function createOverlays(map: MLMap, o: { beforeId: string; placesBeforeId: string; reducedMotion: () => boolean }): OverlaysHandle {
  const app0 = getApp();
  let mood: Mood = app0.mood;
  let layout: Layout = app0.layout;
  let group = app0.group;
  let contoursFlag = app0.layers.contours;
  let destroyed = false;
  const reduced = (): boolean => {
    try {
      return !!o.reducedMotion();
    } catch {
      return false;
    }
  };
  const origin: LngLat = (app0.data?.origin.coord as LngLat | undefined) ?? [73.960862, 18.605185];
  const site = siteLabelPoint(app0.geo?.site, origin);

  const anim = new Animator();
  const has = (id: string): boolean => {
    try {
      return !!map.getLayer(id);
    } catch {
      return false;
    }
  };
  const paint = (id: string, k: string, v: unknown): void => {
    if (destroyed || !has(id)) return;
    try {
      (map as unknown as { setPaintProperty(l: string, k: string, v: unknown): void }).setPaintProperty(id, k, v);
    } catch (err) {
      console.warn("[overlays]", id, k, err);
    }
  };
  const layoutProp = (id: string, k: string, v: unknown): void => {
    if (destroyed || !has(id)) return;
    (map as unknown as { setLayoutProperty(l: string, k: string, v: unknown): void }).setLayoutProperty(id, k, v);
  };
  const setVisible = (id: string, v: boolean): void => {
    if (!has(id)) return;
    const want = v ? "visible" : "none";
    if (map.getLayoutProperty(id, "visibility") !== want) map.setLayoutProperty(id, "visibility", want);
  };

  const k = (): { k: number; halo: number } => (layout === "kiosk" ? { k: 1.2, halo: 0.5 } : { k: 1, halo: 0 });

  /* ── sources ─────────────────────────────────────────────────────────── */

  const rings = ringFeatures(site, RING_KM);
  const ringsData: FeatureCollection<LineString | Point> = { type: "FeatureCollection", features: [...rings.lines.features, ...rings.labels.features] };
  const roadsData = withLabels(app0.geo?.roads, roadLabelText);
  // A corridor that is a place is labelled with that place's name and status, as the list, card and
  // About sheet name it (QA L3-7); the Outer Ring Road (no place) keeps its data name.
  const placeById = new Map((app0.data?.places ?? []).map((p) => [p.id, p]));
  const futureData = withLabels(app0.geo?.future, (p) => {
    const pl = p.placeId ? placeById.get(p.placeId) : undefined;
    return futureLabelText(pl ? { name: pl.name, status: pl.status } : p);
  });
  const addSource = (id: string, spec: Parameters<MLMap["addSource"]>[1]): void => {
    if (!map.getSource(id)) map.addSource(id, spec);
  };
  addSource(SOURCE_IDS.contoursLit, { type: "geojson", data: EMPTY_FC, maxzoom: 16, tolerance: 0.2, buffer: 16 });
  addSource(SOURCE_IDS.site, { type: "geojson", data: sitePolygon(app0.geo?.site), maxzoom: 16 });
  addSource(SOURCE_IDS.rings, { type: "geojson", data: ringsData as FeatureCollection, maxzoom: 14, tolerance: 0.3 });
  addSource(SOURCE_IDS.roads, { type: "geojson", data: roadsData as FeatureCollection, promoteId: "id", maxzoom: 15, tolerance: 0.3, buffer: 64 });
  addSource(SOURCE_IDS.future, { type: "geojson", data: futureData as FeatureCollection, promoteId: "id", maxzoom: 15, tolerance: 0.3, buffer: 64 });

  /* ── layers ──────────────────────────────────────────────────────────── */

  function layers(m: Mood): { overlays: LayerSpecification[]; places: LayerSpecification[] } {
    const ov = MOODS[m].overlays;
    const pl = MOODS[m].places;
    const c = MOODS[m].contours;
    const { k: kk, halo } = k();
    const colTr = { duration: MOOD_TRANSITION_MS, delay: 0 };
    return {
      overlays: [
        {
          id: LAYER_IDS.veil,
          type: "background",
          layout: { visibility: "none" },
          paint: { "background-color": cssVar(m, "--ld-veil", "rgba(0,0,0,0)"), "background-color-transition": colTr, "background-opacity": 0, "background-opacity-transition": NO_TR },
        },
        {
          id: LAYER_IDS.contoursLit,
          type: "line",
          source: SOURCE_IDS.contoursLit,
          layout: { "line-join": "round", "line-cap": "butt", visibility: "none" },
          paint: {
            "line-color": cssVar(m, "--ld-contour-lit", ov.roadHighlight),
            "line-color-transition": colTr,
            "line-width": ["case", ["get", "major"], c.majorWidth + 0.4, c.minorWidth + 0.4] as Expr,
            "line-opacity": ["get", "w"] as Expr,
            "line-layer-opacity": 0,
            "line-layer-opacity-transition": NO_TR,
          },
        },
        {
          id: LAYER_IDS.siteFill,
          type: "fill",
          source: SOURCE_IDS.site,
          paint: { "fill-color": ov.siteFill, "fill-opacity": ov.siteFillOpacity, "fill-color-transition": colTr, "fill-opacity-transition": colTr, "fill-layer-opacity": 1, "fill-layer-opacity-transition": NO_TR },
        },
        {
          id: LAYER_IDS.siteLine,
          type: "line",
          source: SOURCE_IDS.site,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": ov.siteLine, "line-color-transition": colTr, "line-width": 1.5, "line-layer-opacity": 1, "line-layer-opacity-transition": NO_TR },
        },
        {
          id: LAYER_IDS.rings,
          type: "line",
          source: SOURCE_IDS.rings,
          filter: ["==", ["geometry-type"], "LineString"] as FilterSpecification,
          maxzoom: 13.5,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": ov.ring,
            "line-color-transition": colTr,
            "line-width": 1,
            "line-dasharray": [2, 6],
            "line-opacity": ["interpolate", ["linear"], ["zoom"], 13, fs("o", 1), 13.5, 0] as Expr,
          },
        },
        {
          id: LAYER_IDS.roadsCase,
          type: "line",
          source: SOURCE_IDS.roads,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": ov.roadCase, "line-color-transition": colTr, "line-width": W.roadCase, "line-opacity": quietFade(QUIET) },
        },
        {
          id: LAYER_IDS.roadsLine,
          type: "line",
          source: SOURCE_IDS.roads,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": ov.road, "line-color-transition": colTr, "line-width": W.road, "line-opacity": quietFade(QUIET) },
        },
        {
          id: OVERLAY_IDS.roadsGlow,
          type: "line",
          source: SOURCE_IDS.roads,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": MOODS[m].light.glow,
            "line-color-transition": colTr,
            "line-width": W.lit(4),
            "line-blur": 6,
            "line-opacity": ["*", 0.35 * glowGain(m), fs("v", 1), fs("g")] as Expr,
          },
        },
        {
          id: OVERLAY_IDS.roadsShade,
          type: "line",
          source: SOURCE_IDS.roads,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": cssVar(m, "--ld-route-shade", "rgba(0,0,0,0)"), "line-color-transition": colTr, "line-width": W.lit(1, shadePx(m)), "line-blur": 1, "line-opacity": LIT },
        },
        {
          id: OVERLAY_IDS.roadsLit,
          type: "line",
          source: SOURCE_IDS.roads,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": ov.roadHighlight, "line-color-transition": colTr, "line-width": W.lit(1), "line-opacity": LIT },
        },
        {
          id: LAYER_IDS.futureLine,
          type: "line",
          source: SOURCE_IDS.future,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": ov.future, "line-color-transition": colTr, "line-width": W.future, "line-dasharray": [3, 2], "line-opacity": FUTURE_OP },
        },
        {
          id: OVERLAY_IDS.roadsLabel,
          type: "symbol",
          source: SOURCE_IDS.roads,
          layout: {
            "symbol-placement": "line",
            "symbol-spacing": 500,
            "text-field": ["get", "label"] as Expr,
            "text-font": [FONTSTACKS.bold],
            "text-size": Math.round(11 * kk * 100) / 100,
            "text-max-angle": 30,
            "text-padding": 4,
          },
          paint: {
            "text-color": pl.label,
            "text-color-transition": colTr,
            "text-halo-color": pl.labelHalo,
            "text-halo-color-transition": colTr,
            "text-halo-width": 1.4 + halo,
            "text-opacity": ["interpolate", ["linear"], ["zoom"], 9.85, LIT, 10.15, ["max", ["*", fs("v", 1), fs("q"), ["-", 1, ["*", 0.6, fs("od")]]], LIT]] as Expr,
          },
        },
        {
          id: LAYER_IDS.futureLabel,
          type: "symbol",
          source: SOURCE_IDS.future,
          layout: {
            "symbol-placement": "line",
            "symbol-spacing": 420,
            "text-field": ["get", "label"] as Expr,
            "text-font": [FONTSTACKS.bold],
            "text-size": Math.round(10 * kk * 100) / 100,
            "text-letter-spacing": 0.12,
            "text-transform": "uppercase",
            // the schematic and OSM lines bend often; 30° left the ring roads unlabelled (QA L3-2)
            "text-max-angle": FUTURE_LABEL_MAX_ANGLE,
            "text-padding": 4,
          },
          paint: {
            "text-color": ov.futureLabel,
            "text-color-transition": colTr,
            "text-halo-color": ov.futureLabelHalo,
            "text-halo-color-transition": colTr,
            "text-halo-width": 1.5 + halo,
            "text-opacity": FUTURE_LABEL_OP,
          },
        },
      ],
      places: [
        {
          id: LAYER_IDS.ringsLabel,
          type: "symbol",
          source: SOURCE_IDS.rings,
          filter: ["==", ["geometry-type"], "Point"] as FilterSpecification,
          maxzoom: 13.5,
          layout: {
            "text-field": ["get", "text"] as Expr,
            "text-font": [FONTSTACKS.bold],
            "text-size": Math.round(10 * kk * 100) / 100,
            "text-letter-spacing": 0.14,
            "text-transform": "uppercase",
            "text-padding": 2,
          },
          paint: {
            "text-color": ov.ringLabel,
            "text-color-transition": colTr,
            "text-halo-color": ov.ringLabelHalo,
            "text-halo-color-transition": colTr,
            "text-halo-width": 1.5 + halo,
            "text-opacity": ringLabelOpacity(),
          },
        },
      ],
    };
  }

  const built = layers(mood);
  const beforeOk = (id: string): string | undefined => (has(id) ? id : undefined);
  for (const l of built.overlays) if (!has(l.id)) map.addLayer(l, beforeOk(o.beforeId));
  for (const l of built.places) if (!has(l.id)) map.addLayer(l, beforeOk(o.placesBeforeId));
  const ownLayers = [...built.overlays, ...built.places].map((l) => l.id);

  /* ── feature-state animators ─────────────────────────────────────────── */

  const roadsFx = new FeatureFx(map, SOURCE_IDS.roads, anim, { v: 1, wm: 1 });
  const futureFx = new FeatureFx(map, SOURCE_IDS.future, anim, { v: 1 });
  const ringsFx = new FeatureFx(map, SOURCE_IDS.rings, anim, { o: 1 });
  const roadIds = roadsData.features.map((f) => String((f.properties as { id: string }).id));
  const futureIds = futureData.features.map((f) => String((f.properties as { id: string }).id));
  /** Corridors that are places of the future-infrastructure category (the bloom's "{n} planned corridors"). */
  const futurePlaceIds = new Set(futureData.features.filter((f) => !!(f.properties as { placeId?: string }).placeId).map((f) => String((f.properties as { id: string }).id)));

  const ms = (enter: number): number => (reduced() ? 160 : enter);
  const easeIn = (name: EaseName = "settle") => easeBy(reduced() ? "linear" : name);
  const easeOut = () => easeBy(reduced() ? "linear" : "leave");

  /* ── the veil (§D.10) ────────────────────────────────────────────────── */

  const veil: Tween = anim.value(0, (v) => {
    setVisible(LAYER_IDS.veil, v > 0.001);
    paint(LAYER_IDS.veil, "background-opacity", Math.round(v * 1000) / 1000);
  });

  /* ── site outline ────────────────────────────────────────────────────── */

  const siteVis: Tween = anim.value(app0.layers.boundary ? 1 : 0, () => applySite());
  const siteFill: Tween = anim.value(1, () => applySite());
  function applySite(): void {
    const v = siteVis.v;
    setVisible(LAYER_IDS.siteLine, v > 0.001);
    setVisible(LAYER_IDS.siteFill, v * siteFill.v > 0.001);
    paint(LAYER_IDS.siteLine, "line-layer-opacity", Math.round(v * 1000) / 1000);
    paint(LAYER_IDS.siteFill, "fill-layer-opacity", Math.round(v * siteFill.v * 1000) / 1000);
  }
  applySite();

  /* ── rings ───────────────────────────────────────────────────────────── */

  let ringsShown = app0.layers.rings;
  const ringIdx = RING_KM.map((_, i) => i);
  const ringsAll = (v: number): void => {
    for (const i of ringIdx) {
      ringsFx.set(i, "o", v);
      ringsFx.set(ringLabelId(i), "o", v);
    }
  };
  ringsAll(ringsShown ? 1 : 0);
  const ringVisibility = (): void => {
    const any = ringIdx.some((i) => ringsFx.target(i, "o") > 0 || ringsFx.get(i, "o") > 0.001);
    setVisible(LAYER_IDS.rings, any);
    setVisible(LAYER_IDS.ringsLabel, any);
  };
  ringVisibility();
  let ringsSeq = 0;

  /* ── corridors ───────────────────────────────────────────────────────── */

  let roadsVisible = true;
  let roadHighlight: string | null = null;
  let roadBloom: string[] = [];
  let futureVisible = app0.layers.future;
  let futureHighlight: string | null = null;
  let futureBloom = false;

  function applyRoads(animate = true): void {
    const d = animate ? ms(DUR.unhurried) : 0;
    const dOut = animate ? ms(exitMs(DUR.unhurried)) : 0;
    const bloom = new Set(roadBloom);
    for (const id of roadIds) {
      const hero = roadHighlight === id;
      const lit = hero || bloom.has(id);
      const set = (key: string, v: number) => {
        const up = v > roadsFx.target(id, key);
        void roadsFx.to(id, key, v, up ? d : dOut, up ? easeIn() : easeOut());
      };
      set("v", roadsVisible ? 1 : 0);
      set("q", group === "A" ? 1 : 0);
      set("a", lit ? 1 : 0);
      set("g", hero ? 1 : 0);
      set("od", roadHighlight && !hero ? 1 : 0);
      if (lit) roadsFx.set(id, "wm", hero ? 1 : 0.8);
    }
  }
  function applyFuture(animate = true): void {
    const d = animate ? ms(DUR.unhurried) : 0;
    const dOut = animate ? ms(exitMs(DUR.unhurried)) : 0;
    for (const id of futureIds) {
      const sel = futureHighlight === id;
      // The bloom lights the category's corridors (the count the summary gives); a corridor that is
      // not one of its places (the Outer Ring Road) recedes with the others (§E.4, QA L3-7).
      const bloomed = futureBloom && futurePlaceIds.has(id);
      const set = (key: string, v: number) => {
        const up = v > futureFx.target(id, key);
        void futureFx.to(id, key, v, up ? d : dOut, up ? easeIn() : easeOut()).then((ok) => {
          if (ok && key === "v") futureLayerVisibility();
        });
      };
      set("v", futureVisible ? 1 : 0);
      set("s", sel ? 1 : 0);
      set("b", bloomed ? 1 : 0);
      set("od", (futureHighlight && !sel) || (futureBloom && !futureHighlight && !bloomed) ? 1 : 0);
    }
    futureLayerVisibility();
    futureLabelPriority();
  }
  /**
   * §D.7 / §E.4: the selected or bloomed corridors carry their "NAME · STATUS" label. At rest the
   * label layer sits under the basemap labels (the map stays quiet). While corridors are asked about,
   * the layer shows only theirs and moves above the basemap labels; a selected corridor's label may
   * also overlap other symbols, so ring labels and pin twins never hide the line chosen (QA L3-2). A
   * label still needs room along its own line: a short corridor at a regional zoom stays unlabelled.
   */
  let futureLabelKey = "";
  function futureLabelPriority(): void {
    const ids = futureHighlight ? [futureHighlight] : futureBloom ? futureIds.filter((id) => futurePlaceIds.has(id)) : [];
    const key = ids.join(",");
    if (key === futureLabelKey || destroyed || !has(LAYER_IDS.futureLabel)) return;
    const up = ids.length > 0;
    const before = up ? LAYER_IDS.ringsLabel : o.beforeId;
    try {
      if (has(before)) map.moveLayer(LAYER_IDS.futureLabel, before);
      map.setFilter(LAYER_IDS.futureLabel, up ? (["in", ["get", "id"], ["literal", ids]] as FilterSpecification) : null);
      // only the one selected corridor may overlap other symbols; bloom labels still give way
      layoutProp(LAYER_IDS.futureLabel, "text-allow-overlap", !!futureHighlight);
      // the PMC link turns sharply at the site; the asked-about label may follow it round the bend
      layoutProp(LAYER_IDS.futureLabel, "text-max-angle", up ? 90 : FUTURE_LABEL_MAX_ANGLE);
      futureLabelKey = key;
    } catch (err) {
      console.warn("[overlays] future label emphasis", err);
    }
  }
  function futureLayerVisibility(): void {
    const any = futureVisible || futureIds.some((id) => futureFx.get(id, "v") > 0.001);
    setVisible(LAYER_IDS.futureLine, any);
    setVisible(LAYER_IDS.futureLabel, any);
  }
  applyRoads(false);
  applyFuture(false);

  /* ── lit contours (§D.10) ────────────────────────────────────────────── */

  // Indexed once at attach (≈ 13 ms for the 100 k vertices, under the loader), so every select builds in ~1 ms.
  const tIdx = performance.now();
  const contourIdx: ContourIndex = indexContours(app0.geo?.contours);
  const litIndexMs = performance.now() - tIdx;
  const getIndex = (): ContourIndex => contourIdx;

  let litSeq = 0;
  let litHasData = false;
  let litSegments = 0;
  let litBuildMs = 0;
  let litSwap: ReturnType<typeof setTimeout> | null = null;
  const litFlag: Tween = anim.value(contoursFlag ? 1 : 0, () => applyLit());
  const lit: Tween = anim.value(0, () => applyLit());
  function applyLit(): void {
    const v = Math.round(lit.v * litFlag.v * 1000) / 1000;
    setVisible(LAYER_IDS.contoursLit, v > 0.001 && litHasData);
    paint(LAYER_IDS.contoursLit, "line-layer-opacity", v);
  }
  const setLitData = (fc: FeatureCollection): void => {
    const src = map.getSource(SOURCE_IDS.contoursLit) as GeoJSONSource | undefined;
    if (!src) return;
    void src.setData(fc);
    litHasData = fc.features.length > 0;
  };

  function lightContours(at: LngLat | null, lo?: Parameters<OverlaysHandle["lightContours"]>[1]): void {
    if (destroyed) return;
    const seq = ++litSeq;
    if (litSwap) clearTimeout(litSwap);
    litSwap = null;
    const r = reduced();
    const outMs = r ? 160 : exitMs(DUR.calm); // 294 leave
    if (at && !lo) lo = { zoom: map.getZoom() };
    if (!at || !lo) {
      void lit.to(0, lit.v > 0 ? outMs : 0, easeOut()).then((ok) => {
        if (ok && seq === litSeq) {
          setLitData(EMPTY_FC);
          applyLit();
        }
      });
      return;
    }
    const radiusPx = lo.radiusPx ?? (layout === "kiosk" ? 130 : 110);
    const t0 = performance.now();
    const build = buildLitContours(getIndex(), at, { zoom: lo.zoom, radiusPx });
    litBuildMs = performance.now() - t0;
    litSegments = build.segments;
    const rest = Math.max(0, Math.min(1, lo.rest ?? 0.6));
    const delay = Math.max(0, lo.delayMs ?? 0);
    const rise = (): void => {
      if (seq !== litSeq || destroyed) return;
      setLitData(build.data as FeatureCollection);
      if (r) {
        lit.set(0);
        void lit.to(rest, 160, easeBy("linear"), delay);
        return;
      }
      lit.set(0);
      void lit.to(1, Math.max(1, lo.durationMs ?? 1600), easeBy(lo.ease ?? "settle"), delay).then((ok) => {
        if (!ok || seq !== litSeq) return;
        void lit.to(rest, 2400, easeBy("breathe")).then((ok2) => {
          if (ok2 && seq === litSeq && rest === 0) {
            setLitData(EMPTY_FC);
            applyLit();
          }
        });
      });
    };
    // A → B: A's contours go out first (294 leave), then B's are swapped in, hidden, to rise on time.
    if (lit.v > 0.001) {
      const wait = outMs;
      void lit.to(0, wait, easeOut());
      litSwap = setTimeout(() => {
        litSwap = null;
        rise();
      }, wait);
    } else rise();
  }

  /* ── shader warm-up (L2-04) ──────────────────────────────────────────── */

  // The lit contours are the first line layer with a data-driven width and opacity, and the first layer
  // drawn at a fractional layer opacity (MapLibre's offscreen "layerOpacity" pass). MapLibre links a
  // program at its first draw, synchronously: 100–270 ms at the first arrival or the intro's site beat.
  // Draw them once now, under the loader: one short run at the centre, at a layer opacity no pixel shows
  // (0.001 of a colour rounds to nothing), then put everything back. A real lightContours() call in the
  // meantime takes over (litSeq).
  let warmOff: (() => void) | null = null;
  (function warmPrograms(): void {
    const seq = litSeq;
    const c = map.getCenter();
    const d = 0.002;
    setLitData({
      type: "FeatureCollection",
      features: [{ type: "Feature", properties: { w: 1, major: true }, geometry: { type: "LineString", coordinates: [[c.lng - d, c.lat], [c.lng + d, c.lat + d / 2]] } }],
    });
    setVisible(LAYER_IDS.contoursLit, true);
    paint(LAYER_IDS.contoursLit, "line-layer-opacity", 0.001);
    let frames = 0;
    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      map.off("render", onRender);
      clearTimeout(cap);
      warmOff = null;
      if (destroyed || seq !== litSeq) return; // a real run owns the layer now
      setLitData(EMPTY_FC);
      applyLit();
    };
    // two frames after the source has its tile: the layer has been drawn at least once
    const onRender = (): void => {
      let loaded: boolean;
      try {
        loaded = map.isSourceLoaded(SOURCE_IDS.contoursLit);
      } catch {
        loaded = true; // the source is gone: nothing left to warm
      }
      if (loaded && ++frames >= 2) done();
      else map.triggerRepaint();
    };
    const cap = setTimeout(done, 4000);
    map.on("render", onRender);
    map.triggerRepaint();
    warmOff = done;
  })();

  /* ── store ───────────────────────────────────────────────────────────── */

  const unsub = appStore.subscribe((s, prev) => {
    if (destroyed) return;
    if (s.group !== prev.group) {
      group = s.group;
      applyRoads();
    }
    if (s.layers.contours !== prev.layers.contours) {
      contoursFlag = s.layers.contours;
      void litFlag.to(contoursFlag ? 1 : 0, ms(contoursFlag ? DUR.unhurried : 448), contoursFlag ? easeIn() : easeOut());
    }
    if (s.layout !== prev.layout) {
      layout = s.layout;
      applyTextSizes();
    }
  });

  function applyTextSizes(): void {
    const { k: kk, halo } = k();
    layoutProp(OVERLAY_IDS.roadsLabel, "text-size", Math.round(11 * kk * 100) / 100);
    layoutProp(LAYER_IDS.futureLabel, "text-size", Math.round(10 * kk * 100) / 100);
    layoutProp(LAYER_IDS.ringsLabel, "text-size", Math.round(10 * kk * 100) / 100);
    paint(OVERLAY_IDS.roadsLabel, "text-halo-width", 1.4 + halo);
    paint(LAYER_IDS.futureLabel, "text-halo-width", 1.5 + halo);
    paint(LAYER_IDS.ringsLabel, "text-halo-width", 1.5 + halo);
  }

  /* ── API ─────────────────────────────────────────────────────────────── */

  const api: OverlaysHandle = {
    setBoundary(b): void {
      if (destroyed) return;
      const v = b.visible ? 1 : 0;
      const up = v > siteVis.target;
      void siteVis.to(v, ms(up ? DUR.unhurried : 448), up ? easeIn() : easeOut());
      if (b.fill !== undefined) {
        const f = Math.max(0, Math.min(1, b.fill));
        const fu = f > siteFill.target;
        void siteFill.to(f, ms(fu ? DUR.unhurried : 448), fu ? easeIn() : easeOut());
      }
    },

    setRings(r): Promise<void> {
      if (destroyed) return Promise.resolve();
      const seq = ++ringsSeq;
      ringsShown = r.visible;
      const rd = reduced();
      const jobs: Promise<boolean>[] = [];
      setVisible(LAYER_IDS.rings, true);
      setVisible(LAYER_IDS.ringsLabel, true);
      if (r.visible && r.animate && !rd) {
        // The ripple (§E.7): each ring fades in over 640 settle, 140 apart, its label 240 after it.
        for (const i of ringIdx) {
          ringsFx.set(i, "o", 0);
          ringsFx.set(ringLabelId(i), "o", 0);
          jobs.push(ringsFx.to(i, "o", 1, DUR.unhurried, easeBy("settle"), i * 140));
          jobs.push(ringsFx.to(ringLabelId(i), "o", 1, DUR.unhurried, easeBy("settle"), i * 140 + 240));
        }
      } else {
        const v = r.visible ? 1 : 0;
        const d = rd ? 160 : r.visible ? DUR.unhurried : 448;
        const e = rd ? easeBy("linear") : r.visible ? easeBy("settle") : easeBy("leave");
        for (const i of ringIdx) {
          jobs.push(ringsFx.to(i, "o", v, d, e));
          jobs.push(ringsFx.to(ringLabelId(i), "o", v, d, e));
        }
      }
      return Promise.all(jobs).then(() => {
        if (seq === ringsSeq && !destroyed) ringVisibility();
      });
    },

    setRoads(r): void {
      if (destroyed) return;
      roadsVisible = r.visible;
      if (r.highlight !== undefined) roadHighlight = r.highlight && roadIds.includes(r.highlight) ? r.highlight : null;
      if (r.bloom !== undefined) roadBloom = r.bloom.filter((id) => roadIds.includes(id));
      applyRoads();
    },

    setFuture(f): void {
      if (destroyed) return;
      futureVisible = f.visible;
      if (f.highlight !== undefined) futureHighlight = f.highlight && futureIds.includes(f.highlight) ? f.highlight : null;
      if (f.bloom !== undefined) futureBloom = f.bloom;
      applyFuture();
    },

    setVeil(v, vo): void {
      if (destroyed) return;
      const target = Math.max(0, Math.min(1, v));
      const rd = reduced();
      const up = target > veil.v;
      const dur = rd ? 160 : Math.max(0, vo?.duration ?? (up ? DUR.scene : exitMs(DUR.scene)));
      const ease = rd ? easeBy("linear") : easeBy(vo?.ease ?? (up ? "drift" : "leave"));
      void veil.to(target, dur, ease);
    },

    lightContours,

    setMood(m, mo): void {
      if (destroyed || !MOODS[m]) return;
      mood = m;
      const dur = reduced() ? 160 : Math.max(0, mo?.duration ?? MOOD_TRANSITION_MS);
      const next = layers(m);
      const all = [...next.overlays, ...next.places] as Array<LayerSpecification & { paint?: Record<string, unknown> }>;
      for (const l of all) {
        for (const [key, value] of Object.entries(l.paint ?? {})) {
          if (key.endsWith("-transition")) continue;
          // ticker-driven values stay where the ticker has them
          if (key === "background-opacity" || key === "line-layer-opacity" || key === "fill-layer-opacity") continue;
          if (/-color$/.test(key)) paint(l.id, `${key}-transition`, { duration: dur, delay: 0 });
          paint(l.id, key, value);
        }
      }
    },

    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      warmOff?.();
      unsub();
      if (litSwap) clearTimeout(litSwap);
      roadsFx.destroy();
      futureFx.destroy();
      ringsFx.destroy();
      anim.destroy();
      try {
        for (const id of [...ownLayers].reverse()) if (map.getLayer(id)) map.removeLayer(id);
        for (const id of [SOURCE_IDS.contoursLit, SOURCE_IDS.site, SOURCE_IDS.rings, SOURCE_IDS.roads, SOURCE_IDS.future]) if (map.getSource(id)) map.removeSource(id);
      } catch {
        /* the map is already gone */
      }
    },

    debug(): OverlaysDebug {
      return {
        veil: veil.v,
        lit: lit.v * litFlag.v,
        litSegments,
        litBuildMs,
        litIndexMs,
        rings: ringIdx.map((i) => ringsFx.get(i, "o")),
        site: { visible: siteVis.v, fill: siteFill.v },
        roadsHighlight: roadHighlight,
        roadsBloom: [...roadBloom],
        futureHighlight,
        group,
      };
    },
  };
  return api;
}

// The contract's factory signature (the controller imports createOverlays through it).
const _factory: CreateOverlays = createOverlays;
void _factory;
